import {
  BookOutlined,
  BuildFilled,
  DashboardOutlined,
  DatabaseOutlined,
  EditOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  RocketOutlined,
  SettingOutlined,
  ToolOutlined,
} from '@ant-design/icons'
import { useCurrentOrganisation } from 'Hooks/useCurrentOrganisation'
import { HomeRow, MenuTitle } from 'Layouts/styled'
import { Avatar, Row, Space, Spin, Tag, Tooltip } from 'antd'
import { useCallback, useEffect, useMemo, useState } from 'react'
import TagManager from 'react-gtm-module'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { useParams } from 'react-router-dom'
import MenuItem from '../../Components/Menu/MenuItem'
import SubMenu from '../../Components/Menu/SubMenu'
import { Flow } from '../../Models/Flow/types'
import { FlowConfig } from '../../Models/FlowConfig/types'
import { Language } from '../../Models/Languages/types'
import { TemplateType6fcEnum } from '../../Service'
import navMenuStyles from '../../Styles/NavMenu.module.scss'
import AuthBaseLayout from '../AuthBaseLayout'
import Page from '../AuthBaseLayout/Components/Page'

export const FlowLayout = () => {
  const dispatch = useDispatch()
  const flowLayout = useSelector((s: any) => s.flowLayout)

  const [loading, setLoading] = useState<boolean>(false)
  const [isDeployable, setIsDeployable] = useState<boolean>(false)
  const [activeSubMenu, setactiveSubMenu] = useState<string | undefined>(undefined)
  const navigate = useNavigate()
  const params = useParams()
  const location = useLocation()

  const flowTemplateType: TemplateType6fcEnum = flowLayout?.flow?.template_type
  const isLLMEnabled: boolean = flowLayout?.flow?.is_llm

  const showDesignTab = useMemo(() => {
    return flowLayout.flow?.template_type !== 'Application' && !isLLMEnabled
  }, [isLLMEnabled, flowLayout.flow?.template_type])

  const showTrainTab = useMemo(() => {
    return flowLayout.flow?.template_type !== 'Application' && !isLLMEnabled
  }, [isLLMEnabled, flowLayout.flow?.template_type])

  const showDeployTab = useMemo(() => {
    return flowLayout.flow?.template_type !== 'Application' && !isLLMEnabled
  }, [isLLMEnabled, flowLayout.flow?.template_type])

  const { organisation } = useCurrentOrganisation()

  const getOrganisationAvatar = useMemo(() => {
    // TODO: Add Organisation Brand Logo
    const orgInitials = organisation?.name
      ?.split(' ')
      .map((e) => e[0])
      .join('')
    return (
      <Avatar size={'small'} style={{ backgroundColor: 'var(--primary-400)' }}>
        {orgInitials}
      </Avatar>
    )
  }, [organisation])

  const getFlowTypeSlug = (templateType: TemplateType6fcEnum | undefined) => {
    if (templateType === 'Template') {
      return 'templates'
    } else if (templateType === 'SMS Agent') {
      return 'sms-agents'
    }
    return 'voice-agents'
  }

  useEffect(() => {
    dispatch({
      type: 'flowLayout/setFlow',
      uuid: params.flow_uuid,
      organisationUuid: params.organisation_uuid,
      version: params.flow_version,
      subFlow: params.sub_flow,
      setLoading,
      onSuccess: (flow: Flow, version: FlowConfig) => {
        try {
          const ls = localStorage.getItem('user_preferences')
          if (ls !== null) {
            const user_preferences = JSON.parse(ls)
            if (user_preferences[flow.uuid] === undefined) {
              user_preferences[flow.uuid] = {
                [version.version]: {
                  language: 'en-IN',
                },
              }
            }
            if (user_preferences[flow.uuid][version.version] === undefined) {
              user_preferences[flow.uuid][version.version] = {
                language: 'en-IN',
              }
            }
            console.info('setFlow: ', flow.uuid, version.version)
            localStorage.setItem('user_preferences', JSON.stringify(user_preferences))
          } else {
            localStorage.setItem(
              'user_preferences',
              JSON.stringify({
                [flow.uuid]: {
                  [version.version]: {
                    language: 'en-IN',
                  },
                },
              })
            )
          }
        } catch (e) {
          console.info('flow onSuccess Error')
        }

        dispatch({
          type: 'languages/list',
          onSuccess: (languages: Language[]) => {
            let user_language: string
            try {
              const user_preferences = JSON.parse(
                localStorage.getItem('user_preferences') as string
              )
              user_language = user_preferences[flow?.uuid][version?.version].language
            } catch (e) {
              user_language = 'en-IN'
              console.info('language onSuccess error')
            }
            dispatch({
              type: 'flowLayout/setLanguage',
              payload: languages.find((l) => l.code === user_language),
            })
          },
        })
        // Update Google Tag Manager For Monitoring / Analytics
        const tagManagerArgs = {
          dataLayer: {
            FlowID: flow.id,
            FlowName: flow.name,
          },
        }
        TagManager.dataLayer(tagManagerArgs)
      },
      onFailure: (flow: Flow, version: FlowConfig) => {
        const baseUrl = `/clients/${flow.organisation.uuid}/${getFlowTypeSlug(
          flow?.template_type
        )}/${flow.uuid}`
        // Subflow not found
        if (flow && version) {
          navigate(`${baseUrl}/${version.version}/design/start`)
        } else if (flow) {
          navigate(`${baseUrl}/latest_version/design/start`)
        } else {
          navigate(`/clients/${params?.organisation_uuid}/voice-agents`)
        }
        window.location.reload()
      },
    })
  }, [params?.sub_flow])

  useEffect(() => {
    // close issues drawer on layout unmount
    return () => {
      dispatch({
        type: 'yamlValidator/toggleIssuesModal',
        payload: {
          open: false,
        },
      })
    }
  }, [])

  const getCurrentVersion = () => {
    let result
    if (params.flow_version !== undefined) {
      result = params.flow_version
    } else if (flowLayout.version?.version !== undefined) {
      result = flowLayout.version?.version
    } else {
      result = '0.0.1'
    }
    return result
  }

  const isBuildPage = useCallback(() => {
    if (
      location.pathname.includes('design') ||
      location.pathname.includes('configure') ||
      location.pathname.includes('train')
    ) {
      return true
    }
    return false
  }, [location.pathname])

  const isMonitorPage = useCallback(() => {
    if (
      location.pathname.includes('analytics') ||
      (location.pathname.includes('calls') && !location.pathname.includes('golden-calls'))
    ) {
      return true
    }
    return false
  }, [location.pathname])

  const isToolsPage = useCallback(() => {
    if (
      location.pathname.includes('call_stitch') ||
      location.pathname.includes('campaign_manager') ||
      location.pathname.includes('tcn_campaigns') ||
      location.pathname.includes('cti_portal')
    ) {
      return true
    }
    return false
  }, [location.pathname])

  const isTestPage = useCallback(() => {
    if (
      location.pathname.includes('/golden-calls') ||
      location.pathname.includes('/pipeline') ||
      location.pathname.includes('/test')
    ) {
      return true
    }
    return false
  }, [location.pathname])

  return (
    <AuthBaseLayout
      collapsibleSideNav={true}
      sideNavTheme="dark"
      className={[navMenuStyles['menu-item'], navMenuStyles['menu-item__dark']].join(' ')}
      sideMenuProps={{
        style: {
          backgroundColor: 'var(--blue-600)',
        },
        theme: 'dark',
        defaultOpenKeys: [],
      }}
      menu={[
        <SubMenu
          key="submenu-build"
          title={'Build'}
          submenuKey={'build'}
          activeKey={activeSubMenu}
          icon={<EditOutlined />}
          defaultOpen={isBuildPage()}
          selected={isBuildPage()}
          theme="dark"
          onOpen={() => setactiveSubMenu('build')}
        >
          {showDesignTab && (
            <MenuItem
              key="design"
              subMenuItem
              to={`${getCurrentVersion()}/design`}
              selected={location.pathname.includes('design')}
              theme="dark"
            >
              Design
            </MenuItem>
          )}
          <MenuItem
            key="configure"
            subMenuItem
            to={`${getCurrentVersion()}/configure`}
            selected={location.pathname.includes('configure')}
            theme="dark"
          >
            Configure
          </MenuItem>
          {showTrainTab && (
            <MenuItem
              key="train"
              subMenuItem
              to={`train`}
              selected={location.pathname.includes('train')}
              theme="dark"
            >
              Train
            </MenuItem>
          )}
        </SubMenu>,
        // @e2e-experimental-testing
        <MenuItem
          key="test"
          to={`test`}
          icon={<ExperimentOutlined />}
          theme="dark"
          selected={isTestPage()}
        >
          <Space align={'center'} wrap>
            Test{' '}
            <Tag color="#E68C04" style={{ borderRadius: '20px' }}>
              Beta
            </Tag>
          </Space>
        </MenuItem>,
        showDeployTab && (
          <MenuItem key="deploy" to={`deploy`} icon={<RocketOutlined />} theme="dark">
            Deploy
          </MenuItem>
        ),
        flowLayout.flow?.template_id ? (
          <MenuItem
            key="app-settings"
            icon={<BuildFilled />}
            to={`/clients/${params.organisation_uuid}/${getFlowTypeSlug(
              flowLayout.flow?.template_type
            )}/${params.flow_uuid}/${flowLayout.version?.version || '0.0.1'}`}
            theme="dark"
          >
            Application Settings
          </MenuItem>
        ) : null,
        ['Voice Agent', 'Application']?.includes(flowTemplateType) ? (
          <SubMenu
            title="Monitor"
            activeKey={activeSubMenu}
            submenuKey={'monitor'}
            icon={<DashboardOutlined />}
            theme="dark"
            selected={isMonitorPage()}
            defaultOpen={isMonitorPage()}
            onOpen={() => setactiveSubMenu('monitor')}
          >
            <MenuItem
              key="analytics"
              to={`analytics`}
              theme="dark"
              selected={location.pathname.includes('analytics')}
              subMenuItem
            >
              Analytics
            </MenuItem>
            {flowLayout.flow?.template_id ? null : (
              <MenuItem
                key="app-settings"
                to={`calls`}
                theme="dark"
                selected={
                  location.pathname.includes('calls') && !location.pathname.includes('golden-calls')
                }
                subMenuItem
              >
                Calls
              </MenuItem>
            )}
          </SubMenu>
        ) : null,
        ['Voice Agent', 'Application']?.includes(flowTemplateType) ? (
          <MenuItem
            key="enhance"
            to={`enhance`}
            icon={<BookOutlined />}
            theme="dark"
            disabled
            style={{
              color: 'var(--neutral-200)',
            }}
          >
            Enhance
          </MenuItem>
        ) : null,
        ['Voice Agent', 'Application']?.includes(flowTemplateType) ? (
          <SubMenu
            title="Tools"
            submenuKey="tools"
            icon={<ToolOutlined />}
            selected={isToolsPage()}
            activeKey={activeSubMenu}
            defaultOpen={isToolsPage()}
            theme="dark"
            onOpen={() => setactiveSubMenu('tools')}
          >
            <MenuItem
              key="call_stitch"
              to={`call_stitch`}
              theme="dark"
              subMenuItem
              disabled
              style={{
                color: 'var(--neutral-200)',
              }}
            >
              Call Stitch
            </MenuItem>
            <MenuItem key="campaign_manager" to={`campaign_manager`} theme="dark" subMenuItem>
              Campaigns India
            </MenuItem>
            {process.env.NODE_ENV === 'development' ||
            (window as any)?.appConfig?.REGION === 'US' ||
            (window as any)?.appConfig?.REGION === 'STAGING' ? (
              <MenuItem key="tcn_campaigns" to={`tcn_campaigns`} theme="dark" subMenuItem>
                Campaigns Phase A
              </MenuItem>
            ) : null}
            <MenuItem
              key="cti"
              to={`cti_portal`}
              theme="dark"
              subMenuItem
              disabled
              style={{
                color: 'var(--neutral-200)',
              }}
            >
              CTI
            </MenuItem>
          </SubMenu>
        ) : null,
        ['Voice Agent', 'Application']?.includes(flowTemplateType) ? (
          <Tooltip title="Catalogue - Coming Soon!">
            <MenuItem
              key="catalogue"
              to={`catalogue`}
              icon={<DatabaseOutlined />}
              theme="dark"
              disabled
              style={{
                color: 'var(--neutral-200)',
              }}
            >
              Catalogue
            </MenuItem>
          </Tooltip>
        ) : null,
        <MenuItem
          key="documentation"
          to={`documentation`}
          icon={<FileTextOutlined />}
          theme="dark"
          disabled
          style={{
            color: 'var(--neutral-200)',
          }}
        >
          Documentation
        </MenuItem>,
      ]}
      bottomMenu={[
        <MenuItem
          key="settings"
          to={`settings`}
          icon={<SettingOutlined />}
          theme="dark"
          style={{
            marginTop: 'auto',
          }}
        >
          Settings
        </MenuItem>,
      ]}
      headerRender={({ theme, collapsed }: any) => {
        return (
          <HomeRow theme={theme}>
            {/* <SkitLogo width={25} height={25} /> */}
            {getOrganisationAvatar}
            {!collapsed ? <MenuTitle theme={theme}>{organisation?.name || ''}</MenuTitle> : null}
          </HomeRow>
        )
      }}
    >
      {flowLayout.flow?.uuid && flowLayout.version?.version && flowLayout.language?.id ? (
        // YamlIssuesDrawer portals here, hence the id="flow-layout-container"
        // is important -> being used in IssuesDrawer.tsx
        <div
          id="flow-layout-container"
          style={{
            width: '100%',
            height: '100vh',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Outlet />
        </div>
      ) : (
        <Page>
          <Row style={{ height: '100%' }} justify="space-around" align="middle">
            <Spin size="large" />
          </Row>
        </Page>
      )}
    </AuthBaseLayout>
  )
}
